import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, money, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Download, Image as ImageIcon, FilePlus2, Landmark, Lock, Mail, MailCheck, MailX } from "lucide-react";

export default function SessionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [amendments, setAmendments] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [imgUrl, setImgUrl] = useState(null);
  const canImages = ["admin", "auditor", "superadmin"].includes(user.role);
  const isAdmin = user.role === "admin" || user.role === "superadmin";

  const loadEmails = () => {
    api.get(`/email-logs?session_id=${id}`).then(({ data }) => setEmailLogs(data)).catch(() => {});
  };
  const load = () => {
    api.get(`/sessions/${id}`).then(({ data }) => setS(data)).catch((e) => { toast.error(apiError(e)); nav("/admin/sessions"); });
    if (canImages) {
      api.get(`/sessions/${id}/amendments`).then(({ data }) => setAmendments(data)).catch(() => {});
      loadEmails();
    }
  };
  useEffect(load, [id]); // eslint-disable-line

  const viewImage = async (checkId) => {
    try {
      const { data } = await api.post(`/sessions/${id}/checks/${checkId}/signed-url`);
      setImgUrl(`${API.replace("/api", "")}${data.url}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!s) return <div className="text-[color:var(--muted)]">Loading…</div>;
  const t = s.computed_totals || s.totals || {};

  return (
    <div className="space-y-6">
      <button className="btn btn-ghost h-10 px-4" onClick={() => nav("/admin/sessions")} data-testid="back-sessions"><ArrowLeft size={16} /> Sessions</button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl">{s.service}</h1>
          <p className="text-[color:var(--ink-2)]">{s.date} · {s.state} {s.locked && <Lock size={13} className="inline" />}</p>
          <p className="text-xs text-[color:var(--muted)] mt-1">Counters: {s.counter1_name} · {s.counter2_name}</p>
        </div>
        {s.locked && <button className="btn btn-secondary h-11" onClick={() => window.open(`${API}/sessions/${id}/pdf`, "_blank")} data-testid="detail-pdf"><Download size={16} /> Count Sheet PDF</button>}
      </div>

      {s.locked && canImages && <EmailStatus logs={emailLogs} sessionId={id} isAdmin={isAdmin} onResent={loadEmails} />}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Box label="Cash" value={money(t.cash_total)} />
        <Box label="Checks" value={money(t.check_total)} />
        <Box label="Check Count" value={t.check_count ?? 0} />
        <Box label="Grand Total" value={money(t.grand_total)} hi />
      </div>

      {s.fund_splits && Object.keys(s.fund_splits).length > 0 && (
        <div className="card p-6"><h3 className="font-semibold mb-3">Fund Splits</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(s.fund_splits).map(([k, v]) => <Box key={k} label={k} value={money(v)} />)}
          </div>
        </div>
      )}

      {s.brinks?.bag_serial && (
        <div className="card p-6 flex items-center gap-3"><Landmark className="text-[color:var(--olive)]" /><div><div className="label">Brinks Bag</div><div className="font-mono font-semibold">{s.brinks.bag_serial}</div><div className="text-xs text-[color:var(--muted)]">Dropped {String(s.brinks.dropped_at).slice(0, 19)}</div></div></div>
      )}

      {/* Check images — admin/auditor only */}
      {canImages && (
        <div className="card p-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><ImageIcon size={16} /> Check Images <span className="text-xs font-normal text-[color:var(--muted)]">(access logged · short-lived URLs)</span></h3>
          {(s.checks || []).length === 0 && <p className="text-sm text-[color:var(--muted)]">No checks.</p>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(s.checks || []).map((c) => (
              <button key={c.id} className="p-4 rounded-lg border border-[color:var(--line)] text-left active:bg-[color:var(--sage-light)]" onClick={() => viewImage(c.id)} data-testid={`view-check-${c.id}`}>
                <div className="font-mono tabnum font-semibold">{money(c.amount)}</div>
                <div className="text-xs text-[color:var(--olive)] mt-1 flex items-center gap-1"><ImageIcon size={12} /> View image</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Amendments */}
      {canImages && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Amendments</h3>
            {isAdmin && s.locked && <AmendButton sessionId={id} onDone={load} />}
          </div>
          {amendments.length === 0 && <p className="text-sm text-[color:var(--muted)]">No amendments. Original record stands.</p>}
          <div className="space-y-3">
            {amendments.map((a) => (
              <div key={a.id} className="p-4 rounded-lg bg-[color:var(--terracotta-light)]" data-testid={`amendment-${a.id}`}>
                <div className="text-sm font-semibold text-[color:var(--terracotta)]">Amendment · {String(a.timestamp).slice(0, 19)}</div>
                <div className="text-sm mt-1">Reason: {a.reason}</div>
                <div className="text-xs text-[color:var(--ink-2)] mt-1">{Object.entries(a.corrected_values).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
                <div className="text-xs text-[color:var(--muted)] mt-1">By {a.admin_email}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reconciliation — admin */}
      {isAdmin && s.locked && <Reconcile s={s} onDone={load} />}

      {imgUrl && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setImgUrl(null)} data-testid="image-modal">
          <img src={imgUrl} alt="check" className="max-h-[85vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}

function Box({ label, value, hi }) {
  return <div className={`p-4 rounded-lg ${hi ? "bg-[color:var(--olive)] text-white" : "bg-[color:var(--cream)]"}`}><div className={`text-xs uppercase tracking-wider ${hi ? "text-white/70" : "text-[color:var(--muted)]"}`}>{label}</div><div className="font-mono tabnum text-lg font-semibold mt-1">{value}</div></div>;
}

function EmailStatus({ logs, sessionId, isAdmin, onResent }) {
  const [sending, setSending] = useState(false);
  const summary = (logs || []).find((l) => l.template_type === "count_summary");
  let tone, text, IconC;
  if (!summary) {
    tone = "muted"; text = "No count-summary email recorded for this session."; IconC = Mail;
  } else if (summary.status === "sent") {
    tone = "ok"; IconC = MailCheck;
    const dstat = summary.delivery_status && summary.delivery_status !== "sent" ? ` · ${summary.delivery_status}` : "";
    text = `Count summary emailed to Finance · ${String(summary.created_at).slice(0, 19).replace("T", " ")}${dstat}`;
  } else {
    tone = "bad"; IconC = MailX;
    text = `Count summary email failed${summary.error ? ` — ${summary.error}` : ""}`;
  }
  const styles = {
    ok: "bg-[color:var(--olive-light)] text-[color:var(--olive)]",
    bad: "bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)]",
    muted: "bg-[color:var(--cream)] text-[color:var(--muted)]",
  }[tone];
  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/resend-summary`);
      if (data.status === "sent") toast.success("Count summary re-sent to Finance");
      else toast.error(`Resend failed${data.error ? ` — ${data.error}` : ""}`);
      onResent && onResent();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium ${styles}`} data-testid="email-delivery-status">
      <IconC size={16} /> <span className="flex-1">{text}</span>
      {isAdmin && (
        <button className="btn btn-ghost h-9 px-3 text-sm shrink-0" onClick={resend} disabled={sending} data-testid="resend-summary-btn">
          <MailCheck size={15} /> {sending ? "Sending…" : "Resend"}
        </button>
      )}
    </div>
  );
}

function AmendButton({ sessionId, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [field, setField] = useState("grand_total");
  const [val, setVal] = useState("");
  const submit = async () => {
    try {
      await api.post(`/sessions/${sessionId}/amend`, { reason, corrected_values: { [field]: Number(val) } });
      toast.success("Amendment appended & Finance Admins alerted");
      setOpen(false); setReason(""); setVal(""); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      <button className="btn btn-secondary h-10 px-4 text-sm" onClick={() => setOpen(true)} data-testid="open-amend"><FilePlus2 size={15} /> Amend</button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div className="card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-1">Append Amendment</h3>
            <p className="text-sm text-[color:var(--ink-2)] mb-4">The original record is preserved. This alerts all Finance Admins.</p>
            <label className="block mb-3"><span className="label block mb-1.5">Field</span>
              <select className="field" value={field} onChange={(e) => setField(e.target.value)} data-testid="amend-field">
                <option value="grand_total">Grand Total</option><option value="cash_total">Cash Total</option><option value="check_total">Check Total</option>
              </select></label>
            <label className="block mb-3"><span className="label block mb-1.5">Corrected Value</span><input className="field font-mono" value={val} onChange={(e) => setVal(e.target.value.replace(/[^0-9.]/g, ""))} data-testid="amend-value" /></label>
            <label className="block mb-4"><span className="label block mb-1.5">Reason (required)</span><textarea className="field h-24 py-2" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="amend-reason" /></label>
            <div className="flex gap-3"><button className="btn btn-ghost flex-1" onClick={() => setOpen(false)}>Cancel</button><button className="btn btn-primary flex-1" disabled={!reason || !val} onClick={submit} data-testid="submit-amend">Append</button></div>
          </div>
        </div>
      )}
    </>
  );
}

function Reconcile({ s, onDone }) {
  const rec = s.reconciliation || {};
  const [pickup, setPickup] = useState(rec.brinks_pickup_at || "");
  const [credited, setCredited] = useState(rec.bank_credited_at || "");
  const [posted, setPosted] = useState(rec.bank_posted_amount ?? "");
  const save = async () => {
    try {
      const { data } = await api.post(`/sessions/${s.id}/reconcile`, {
        brinks_pickup_at: pickup || null, bank_credited_at: credited || null,
        bank_posted_amount: posted === "" ? null : Number(posted),
      });
      toast.success(data.variance_flag ? `Saved — variance ${money(data.variance)} flagged` : "Reconciliation saved");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="card p-6" data-testid="reconcile-card">
      <h3 className="font-semibold mb-3">Reconciliation</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block"><span className="label block mb-1.5">Brinks Pickup</span><input type="date" className="field" value={pickup} onChange={(e) => setPickup(e.target.value)} data-testid="rec-pickup" /></label>
        <label className="block"><span className="label block mb-1.5">Bank Credited</span><input type="date" className="field" value={credited} onChange={(e) => setCredited(e.target.value)} data-testid="rec-credited" /></label>
        <label className="block"><span className="label block mb-1.5">Bank Posted $</span><input className="field font-mono" value={posted} onChange={(e) => setPosted(e.target.value.replace(/[^0-9.]/g, ""))} data-testid="rec-posted" /></label>
      </div>
      {rec.variance != null && <div className={`mt-3 text-sm font-semibold ${rec.variance_flag ? "text-[color:var(--terracotta)]" : "text-[color:var(--olive)]"}`}>Variance: {money(rec.variance)}</div>}
      <button className="btn btn-primary mt-4 h-11 px-6" onClick={save} data-testid="save-reconcile">Save Reconciliation</button>
    </div>
  );
}
